import {useState} from 'react';
import styled from 'styled-components';
import {useDroppable} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import type {Ticket} from '../../types/ticket';
import type {MentionUser} from '../../hooks/useMentionUsers';
import type {BoardColumnSpec} from '../../constants/boardColumns';
import {TicketCard} from './TicketCard';

interface Props {
	spec: BoardColumnSpec;
	tickets: Ticket[];
	canDragTicket: (t: Ticket) => boolean;
	showDropPlaceholder?: boolean;
	users?: MentionUser[];
	onAssign?: (ticketId: string, userId: string | null, note?: string) => void;
	onDelete?: (ticketId: string) => void;
}

const Column = styled.section<{$highlighted: boolean}>`
	display: flex;
	flex-direction: column;
	gap: 8px;
	border-radius: 8px;
	background: #f1f5f9;
	padding: 12px;
	transition: box-shadow 0.15s;
	width: 100%;
	${p => p.$highlighted && 'box-shadow: 0 0 0 2px #6259ff;'}

	@media (min-width: 640px) {
		width: calc(50% - 0.5rem);
	}
	@media (min-width: 768px) {
		width: 288px;
		min-width: 280px;
	}
`;

const Header = styled.header`
	display: flex;
	cursor: pointer;
	align-items: center;
	justify-content: space-between;
	padding: 4px;
	@media (min-width: 640px) {
		cursor: default;
	}
`;

const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const Dot = styled.span<{$color: string}>`
	display: inline-block;
	height: 8px;
	width: 8px;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: ${p => p.$color};
`;

const ColumnTitle = styled.h3`
	margin: 0;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 600;
	color: #334155;
`;

const HeaderRight = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const CountBadge = styled.span`
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: #ffffff;
	padding: 2px 8px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #475569;
`;

const Chevron = styled.svg<{$open: boolean}>`
	height: 16px;
	width: 16px;
	color: #94a3b8;
	transition: transform 0.15s;
	transform: rotate(${p => (p.$open ? '180deg' : '0')});
	@media (min-width: 640px) {
		display: none;
	}
`;

const CardList = styled.div<{$collapsed: boolean}>`
	display: ${p => (p.$collapsed ? 'none' : 'flex')};
	flex-direction: column;
	gap: 8px;
	min-height: 40px;
	@media (min-width: 640px) {
		display: flex;
	}
`;

const EmptyText = styled.p`
	margin: 0;
	padding: 24px 8px;
	text-align: center;
	font: ${p => p.theme.ticketing.font.body3};
	color: #94a3b8;
`;

const DropPlaceholder = styled.div`
	min-height: 132px;
	border-radius: 8px;
	border: 2px dashed #6259ff;
	background: rgb(238 237 255 / 0.7);
`;

export function BoardColumn({spec, tickets, canDragTicket, showDropPlaceholder = false, users, onAssign, onDelete}: Props) {
	const {setNodeRef, isOver} = useDroppable({id: `column:${spec.status}`});
	const ids = tickets.map(t => t.id);
	const [collapsed, setCollapsed] = useState(true);

	return (
		<Column ref={setNodeRef} aria-label={spec.label} $highlighted={isOver || showDropPlaceholder}>
			<Header onClick={() => setCollapsed(c => !c)}>
				<HeaderLeft>
					<Dot $color={spec.dotColor} aria-hidden="true" />
					<ColumnTitle>{spec.label}</ColumnTitle>
				</HeaderLeft>
				<HeaderRight>
					<CountBadge>{tickets.length}</CountBadge>
					<Chevron $open={!collapsed} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
						<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
					</Chevron>
				</HeaderRight>
			</Header>

			<SortableContext items={ids} strategy={verticalListSortingStrategy}>
				<CardList $collapsed={collapsed}>
					{tickets.length === 0 && !showDropPlaceholder ? (
						<EmptyText>No tickets</EmptyText>
					) : (
						tickets.map(t => (
							<TicketCard
								key={t.id}
								ticket={t}
								draggable={canDragTicket(t)}
								users={users}
								onAssign={onAssign}
								onDelete={onDelete}
							/>
						))
					)}
					{showDropPlaceholder && <DropPlaceholder aria-label={`Drop ticket in ${spec.label}`} />}
				</CardList>
			</SortableContext>
		</Column>
	);
}
